import React, { useState, useEffect } from 'react'
import { Building, Users, Target, Clock } from 'lucide-react'
import { homepageContentApi } from '../../services/api'

const iconOptions = [Building, Users, Target, Clock]
const iconNames = ['Building', 'Users', 'Target', 'Clock']

const iconMap: Record<string, React.ReactNode> = {
  'Building': <Building className="w-12 h-12 text-blue-600" />,
  'Users': <Users className="w-12 h-12 text-blue-600" />,
  'Target': <Target className="w-12 h-12 text-blue-600" />,
  'Clock': <Clock className="w-12 h-12 text-blue-600" />,
}

// 后备默认数据
const defaultContent = {
  title_zh: '关于惠亚建筑工程有限公司',
  title_vi: 'Về Công Ty TNHH Xây Dựng Công Trình Huệ Á',
  description_zh: '惠亚建筑工程有限公司是一家立足越南北宁的专业机电与暖通工程服务公司，主要服务于工业厂房、外资企业及中资工厂项目。公司专注于机电安装、暖通空调、通风系统及厂房改造工程，可承接新建项目、改造项目及各类机电分包工程。',
  description_vi: 'Công Ty TNHH Xây Dựng Công Trình Huệ Á là một công ty dịch vụ kỹ thuật cơ điện và HVAC chuyên nghiệp đặt tại Bắc Ninh Việt Nam, chủ yếu phục vụ các dự án nhà máy công nghiệp, doanh nghiệp nước ngoài và nhà máy Trung Quốc. Công ty tập trung vào lắp đặt cơ điện, HVAC, hệ thống thông gió và cải tạo nhà xưởng, có thể đảm nhận các dự án xây mới, cải tạo và các gói thầu cơ điện phụ khác nhau.',
  image_url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  features: [
    { title_zh: '专业服务', title_vi: 'Dịch vụ chuyên nghiệp', description_zh: '专注机电安装、暖通空调、通风系统及厂房改造工程', description_vi: 'Tập trung vào lắp đặt cơ điện, HVAC, hệ thống thông gió và cải tạo nhà xưởng' },
    { title_zh: '稳定团队', title_vi: 'Đội ngũ ổn định', description_zh: '拥有稳定的本地施工班组，熟悉越南工业区施工规范', description_vi: 'Có đội ngũ thi công địa phương ổn định, quen thuộc với quy chuẩn thi công khu công nghiệp Việt Nam' },
    { title_zh: '核心优势', title_vi: 'Ưu điểm cốt lõi', description_zh: '快速进场、灵活施工及夜间施工能力，致力提供高效可靠服务', description_vi: 'Khả năng triển khai nhanh, thi công linh hoạt và thi công ban đêm, cam kết cung cấp dịch vụ hiệu quả và đáng tin cậy' },
    { title_zh: '长期合作', title_vi: 'Hợp tác lâu dài', description_zh: '致力于为客户提供高效、可靠、长期合作的工程服务', description_vi: 'Cam kết cung cấp dịch vụ kỹ thuật hiệu quả, đáng tin cậy và hợp tác lâu dài cho khách hàng' }
  ]
}

const businessScopeZH = ['电气系统工程', '暖通与空调系统工程', '通风系统工程', '建筑装饰及改造工程', '地面防水及地坪漆工程']
const businessScopeVI = ['Hệ thống kỹ thuật điện', 'Hệ thống HVAC', 'Hệ thống thông gió', 'Trang trí và cải tạo công trình', 'Chống thấm sàn và sơn sàn']

const CompanyIntro = () => {
  const [isVietnamese, setIsVietnamese] = useState(false)
  const [data, setData] = useState(defaultContent)
  const [loaded, setLoaded] = useState(false)

  // 从 API 加载数据
  useEffect(() => {
    Promise.all([
      homepageContentApi.getBySection('company_intro'),
      homepageContentApi.getBySection('company_intro_features')
    ]).then(([introRes, featuresRes]) => {
      let newData = { ...defaultContent }

      if (introRes.success && introRes.data?.length > 0) {
        const item = introRes.data[0]
        newData.title_zh = item.title_zh || defaultContent.title_zh
        newData.title_vi = item.title_vi || defaultContent.title_vi
        newData.description_zh = item.content_zh || defaultContent.description_zh
        newData.description_vi = item.content_vi || defaultContent.description_vi
        newData.image_url = item.image_url || defaultContent.image_url
      }

      if (featuresRes.success && featuresRes.data?.length > 0) {
        const item = featuresRes.data[0]
        try {
          const parsed = JSON.parse(item.content_zh || '[]')
          if (Array.isArray(parsed) && parsed.length > 0) {
            newData.features = parsed.map((f: any, idx: number) => ({
              ...f,
              iconName: iconNames[idx % iconNames.length]
            }))
          }
        } catch { /* 使用默认值 */ }
      }

      setData(newData)
      setLoaded(true)
    }).catch(() => {
      setLoaded(true) // API 失败时使用默认值
    })
  }, [])

  return (
    <section className="section-padding bg-gray-50 rounded-2xl">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 md:mb-12">
          <div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 animate-fade-in">
              {isVietnamese ? data.title_vi : data.title_zh}
            </h2>
            <div className="h-1 w-16 md:w-24 bg-blue-600 rounded-full"></div>
          </div>
          <button
            onClick={() => setIsVietnamese(!isVietnamese)}
            className="btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-2 whitespace-nowrap"
          >
            {isVietnamese ? '中文 / Tiếng Trung' : 'Tiếng Việt / 越南语'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12 md:mb-16">
          <div className="animate-slide-in-left">
            <p className="text-sm md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8 whitespace-pre-line">
              {isVietnamese ? data.description_vi : data.description_zh}
            </p>
            <div className="bg-blue-50 p-4 md:p-6 rounded-xl">
              <h3 className="text-base md:text-xl font-semibold text-blue-800 mb-3 md:mb-4">
                {isVietnamese ? 'Phạm vi kinh doanh' : '主营业务范围'}
              </h3>
              <ul className="space-y-2">
                {(isVietnamese ? businessScopeVI : businessScopeZH).map((item, index) => (
                  <li key={index} className="flex items-center text-sm md:text-base text-gray-700">
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-600 rounded-full mr-2 md:mr-3 flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="animate-slide-in-right">
            <img 
              src={data.image_url}
              alt={isVietnamese ? data.title_vi : data.title_zh}
              className="w-full h-auto rounded-2xl shadow-lg"
            />
            <div className="mt-4 md:mt-6 p-3 md:p-4 bg-white rounded-xl shadow-sm">
              <p className="text-xs md:text-sm text-gray-600 italic">
                {isVietnamese 
                  ? "Chuyên phục vụ nhà máy công nghiệp, doanh nghiệp nước ngoài và nhà máy Trung Quốc tại Bắc Ninh và các tỉnh lân cận."
                  : "专业服务工业厂房、外资企业及中资工厂项目，覆盖北宁及周边省份。"}
              </p>
            </div>
          </div>
        </div>

        {/* 特色功能卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {data.features.map((feature, index) => (
            <div 
              key={index} 
              className="card animate-scale-in p-4 md:p-6"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-blue-100 rounded-full">
                  {iconMap[feature.iconName || iconNames[index % iconNames.length]] || iconMap['Building']}
                </div>
                <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">
                  {isVietnamese ? feature.title_vi : feature.title_zh}
                </h3>
                <p className="text-xs md:text-base text-gray-600">
                  {isVietnamese ? feature.description_vi : feature.description_zh}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default CompanyIntro
